import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MOCK_STORAGE_USED_BYTES } from '@/features/settings/mockData';
import type {
  PrimeSubscription,
  PrivacySettings,
  PushNotificationKey,
  PushNotificationPreferences,
} from '@/features/settings/types';

const DEFAULT_PUSH_NOTIFICATIONS: PushNotificationPreferences = {
  newPosts: true,
  likes: true,
  comments: true,
  messages: true,
  following: true,
  classJoins: true,
};

const DEFAULT_PRIVACY: PrivacySettings = {
  accountPrivate: false,
  showOnExplore: true,
  receiveComments: true,
  allowDownloads: true,
  messageFrom: 'anyone',
};

interface SettingsState {
  pushNotifications: PushNotificationPreferences;
  privacy: PrivacySettings;
  prime: PrimeSubscription;
  storageUsedBytes: number;
  networkStatus: 'connected' | 'offline' | 'connecting';
  setPushNotification: (key: PushNotificationKey, enabled: boolean) => void;
  hydratePushNotifications: (patch: Partial<PushNotificationPreferences>) => void;
  setPrivacy: <K extends keyof PrivacySettings>(key: K, value: PrivacySettings[K]) => void;
  hydratePrivacy: (patch: Partial<PrivacySettings>) => void;
  setPrimeActive: (expiresAt: string) => void;
  clearPrime: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      pushNotifications: DEFAULT_PUSH_NOTIFICATIONS,
      privacy: DEFAULT_PRIVACY,
      prime: { isActive: false, expiresAt: null },
      storageUsedBytes: MOCK_STORAGE_USED_BYTES,
      networkStatus: 'connected',

      setPushNotification: (key, enabled) => {
        set((state) => ({
          pushNotifications: { ...state.pushNotifications, [key]: enabled },
        }));
      },

      hydratePushNotifications: (patch) => {
        set((state) => ({
          pushNotifications: { ...state.pushNotifications, ...patch },
        }));
      },

      setPrivacy: (key, value) => {
        set((state) => ({
          privacy: { ...state.privacy, [key]: value },
        }));
      },

      hydratePrivacy: (patch) => {
        set((state) => ({
          privacy: { ...state.privacy, ...patch },
        }));
      },

      setPrimeActive: (expiresAt) => {
        set({ prime: { isActive: true, expiresAt } });
      },

      clearPrime: () => {
        set({ prime: { isActive: false, expiresAt: null } });
      },
    }),
    { name: 'myu-settings-store' },
  ),
);
